import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { MouseEventHandler } from 'react';
import { useHistory } from 'react-router-dom';
import { getPrincipalInitials } from '../../../../../models/NameUtils';
import { getDefaultDateStringValue, sortContactTags } from '../../../../../models/UiUtils';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Column } from '../../../../components-v2/PageLayout';
import { Pill } from '../../../../components-v2/Pill/Pill';
import { ButtonBase } from '../../../../components/Button';
import { KeepInTouchAvatarWrapper } from '../../../../components/contacts/KeepInTouchAvatarWrapper';
import { DataGridRow, DataGridRowItem } from '../../../../components/DataGrid';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../components/HoverCard';
import { EmailIcon } from '../../../../components/svgs/icons/EmailIcon';
import { TextingIcon } from '../../../../components/svgs/icons/TextingIcon';
import { ROUTES } from '../../../../constants/routes';
import { aceGray } from '../../../../styles/colors';
import { bs } from '../../../../styles/styles';
import { useContactsPageAppDrawerQueryParamState } from '../hooks/useContactsPageAppDrawerQueryParamState';
import { ColumnDefinition } from '../hooks/useTableColumnVisibility';
import { getContactFieldValue } from '../utils/contactFieldExtractors';
import { usePeopleTableDisplayName } from '../utils/getPeopleTableDisplayName';
import { VisibilityIndicatorAvatar } from '../VisibilityIndicatorAvatar/VisibilityIndicatorAvatar';
import { styleSheet } from './styles';

interface IProps {
	contact: Api.IContact;
	tagsToHighlight?: string[];
	onToggleSelection: (shiftKey?: boolean) => void;
	isSelected: boolean;
	visibleColumns: ColumnDefinition[];
}

const PeopleRowInternal: React.FC<IProps> = ({
	contact,
	isSelected,
	onToggleSelection,
	tagsToHighlight,
	visibleColumns,
}) => {
	const name = usePeopleTableDisplayName(contact);
	const ownerName = usePeopleTableDisplayName(
		contact.connections?.find(conn => conn?.user?.id === contact?.ownerId)?.user
	);
	const sortedTags = sortContactTags({ tags: contact.tagsCollection ?? [], priorityTags: tagsToHighlight });
	const [visibleTag, ...extraTags] = sortedTags;
	const email = contact.primaryEmail?.value;
	const history = useHistory();

	const avatar = (
		<VisibilityIndicatorAvatar
			fallbackText={getPrincipalInitials(contact)}
			imgSrc={contact?.profilePic}
			size='small'
			visible={contact.visibility !== 'admin'}
		/>
	);
	const drawerState = useContactsPageAppDrawerQueryParamState();

	const handleContactClick: MouseEventHandler = event => {
		// If the user clicks the row once, open the contact info drawer
		if (event.detail === 1) {
			drawerState.setDrawerToContactInfo(contact.id);
		} else {
			// If the user clicks the row twice or more, open the contact info in a new tab
			if (event.ctrlKey || event.metaKey) {
				window.open(window.location.origin + `/#${ROUTES.contacts.people}/${contact.id}?origin=contacts`);
			} else {
				history.push(`${ROUTES.contacts.people}/${contact.id}?origin=contacts`);
			}
		}
	};

	const renderCellContent = (columnId: string) => {
		switch (columnId) {
			case 'name':
				return (
					<>
						<Checkbox
							checked={isSelected}
							id={`people-row-checkbox-${contact.id}`}
							onClick={ev => {
								ev.stopPropagation();
								ev.preventDefault();
								onToggleSelection(ev.shiftKey);
							}}
							size='lg'
						/>
						{contact.userHasKeepInTouch ? <KeepInTouchAvatarWrapper>{avatar}</KeepInTouchAvatarWrapper> : avatar}
						<div className={css(styleSheet.nameContainer)}>
							<div className={css(styleSheet.nameWrapper)}>
								<ButtonBase className={css(styleSheet.contactLink, bs.truncateText)} onClick={handleContactClick}>
									{name}
								</ButtonBase>
								{contact.company?.companyName && (
									<div className={css(styleSheet.subtitle)}> {contact.company?.companyName} </div>
								)}
							</div>
						</div>
					</>
				);
			case 'tags':
				return (
					<div className={css(styleSheet.tagsColumn)}>
						{!!visibleTag && <Pill variant='tag' label={visibleTag.name} color={visibleTag?.color} />}
						{extraTags.length > 0 ? (
							<HoverCard>
								<HoverCardTrigger className={css(bs.brandLink, styleSheet.tagMore)}>
									&nbsp;{`+${extraTags.length} more`}
								</HoverCardTrigger>
								<HoverCardContent styles={[styleSheet.extraTags]}>
									{extraTags.map(tagValue => {
										return <Pill key={tagValue.id} variant='tag' label={tagValue.name} color={tagValue?.color} />;
									})}
								</HoverCardContent>
							</HoverCard>
						) : null}
					</div>
				);
			case 'contactInfo':
				return (
					<div className={css(bs.flex, bs.gap2)}>
						{contact.phoneNumbers?.length ? (
							<HoverCard>
								<HoverCardTrigger>
									<TextingIcon fill={aceGray} />
								</HoverCardTrigger>
								<HoverCardContent styles={[styleSheet.infoPopover]}>
									<Column styles={[bs.gap2]}>
										{contact.phoneNumbers.map(phoneNumber => {
											return (
												<ButtonBase
													className={css(styleSheet.infoLink)}
													key={phoneNumber.value}
													onClick={handleContactClick}
												>
													{`${phoneNumber.label ? phoneNumber.label : 'Phone'}: ${phoneNumber.value}`}
												</ButtonBase>
											);
										})}
									</Column>
								</HoverCardContent>
							</HoverCard>
						) : (
							<div className={css(styleSheet.iconPlaceholder)} />
						)}
						{!!email && (
							<HoverCard>
								<HoverCardTrigger>
									<EmailIcon fillColor={aceGray} />
								</HoverCardTrigger>
								<HoverCardContent styles={[styleSheet.infoPopover]}>
									<ButtonBase className={css(styleSheet.infoLink)} onClick={handleContactClick}>
										{email}
									</ButtonBase>
								</HoverCardContent>
							</HoverCard>
						)}
					</div>
				);
			case 'creationDate':
				return <span>{getDefaultDateStringValue(contact.creationDate)}</span>;
			case 'contactOwner':
				return <span>{ownerName || '--'}</span>;
			case 'lastCommunicationDate':
				return (
					// We only store the date part (not time),
					// so don't try to convert to local time zone as the date might be off by one
					<span>
						{contact.communicationMetadata?.lastCommunicationDate
							? getDefaultDateStringValue(moment.utc(contact.communicationMetadata.lastCommunicationDate))
							: '--'}
					</span>
				);
			default: {
				const value = getContactFieldValue(contact, columnId);
				return <span className={css(styleSheet.defaultCell)}>{value || '--'}</span>;
			}
		}
	};

	return (
		<DataGridRow>
			{visibleColumns.map(column => (
				<DataGridRowItem key={column.id} styles={column.id === 'name' ? [styleSheet.nameColumn] : undefined}>
					{renderCellContent(column.id)}
				</DataGridRowItem>
			))}
		</DataGridRow>
	);
};

export const PeopleRow = observer(PeopleRowInternal);
